import { useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Flag, ChevronLeft, ChevronRight } from "lucide-react";
import { PinDistance } from './gps/PinDistance';

/**
 * SharedHoleInfoCard - Reusable swipeable hole information card
 * Shows hole number, par, yardage, handicap, and course name in a modal overlay
 */
const SharedHoleInfoCard = ({ 
  holeNumber, 
  par, 
  yardage, 
  handicap, 
  courseName,
  courseId,
  onDismiss,
  isVisible,
  teeName
}) => {
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const audioContextRef = useRef(null);

  // Play swoosh sound
  const playSwoosh = useCallback(() => {
    try {
      const audioContext = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.setValueAtTime(300, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(80, audioContext.currentTime + 0.15);
      gainNode.gain.setValueAtTime(0.08, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.15);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.15);
    } catch (e) {}
  }, []);

  // Touch handlers
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setDragStart(e.touches[0].clientX);
    setDragOffset(0);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    setDragOffset(e.touches[0].clientX - dragStart);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    if (Math.abs(dragOffset) > 80) {
      playSwoosh();
      setIsExiting(true);
      setTimeout(() => {
        onDismiss();
        setIsExiting(false);
        setDragOffset(0);
      }, 300);
    } else {
      setDragOffset(0);
    }
    setIsDragging(false);
  };

  if (!isVisible) return null;

  const exitDirection = dragOffset > 0 ? 'right' : 'left';
  const transform = isExiting 
    ? `translateX(${exitDirection === 'right' ? '120%' : '-120%'}) rotate(${exitDirection === 'right' ? '10' : '-10'}deg)`
    : `translateX(${dragOffset}px) rotate(${dragOffset * 0.02}deg)`;

  return createPortal(
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onDismiss()}
    >
      <div
        className={`
          ps-fixed-contrast relative w-72 max-w-[calc(100vw-24px)] max-h-[90dvh] overflow-y-auto bg-gradient-to-br from-emerald-800 to-green-900
          rounded-2xl shadow-2xl border border-emerald-500/40
          transition-all duration-300 ease-out select-none
          ${isExiting ? 'opacity-0' : 'opacity-100 animate-fade-in'}
        `}
        style={{ 
          transform,
          cursor: isDragging ? 'grabbing' : 'grab',
          boxShadow: '0 0 40px rgba(16, 185, 129, 0.3), 0 20px 40px rgba(0,0,0,0.5)'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Top Section with Hole Number */}
        <div className="relative px-5 pt-5 pb-4">
          {/* Corner decorations */}
          <div className="absolute top-3 left-3">
            <Flag className="w-4 h-4 text-emerald-400/60" />
          </div>
          <div className="absolute top-3 right-3">
            <Flag className="w-4 h-4 text-emerald-400/60" />
          </div>
          
          {/* Hole Badge */}
          <div className="text-center">
            <div className="inline-flex flex-col items-center justify-center w-20 h-20 rounded-full bg-emerald-600/40 border-2 border-emerald-400/50 mb-3">
              <span className="text-emerald-300 text-[10px] uppercase tracking-widest font-medium">HOLE</span>
              <span className="text-4xl font-black text-white leading-none">{holeNumber}</span>
            </div>
            
            {/* Course Name */}
            {courseName && (
              <p className="text-emerald-200 text-sm font-medium tracking-wide">{courseName}</p>
            )}
          </div>
        </div>

        {/* Stats Grid - Bottom section (show 2 or 3 columns based on yardage availability) */}
        <div className={`grid ${yardage ? 'grid-cols-3' : 'grid-cols-2'} gap-0 bg-emerald-950/50`}>
          <div className={`py-3 text-center ${yardage ? 'border-r border-emerald-700/30' : ''}`}>
            <p className="text-emerald-400 text-[10px] uppercase tracking-wider mb-0.5">Par</p>
            <p className="text-2xl font-bold text-white">{par || '-'}</p>
          </div>
          {yardage && (
            <div className="py-3 text-center border-r border-emerald-700/30">
              <p className="text-emerald-400 text-[10px] uppercase tracking-wider mb-0.5">Yards</p>
              <p className="text-2xl font-bold text-white">{yardage}</p>
            </div>
          )}
          <div className="py-3 text-center">
            <p className="text-emerald-400 text-[10px] uppercase tracking-wider mb-0.5">HCP</p>
            <p className="text-2xl font-bold text-white">{handicap || '-'}</p>
          </div>
        </div>

        {/* Tee indicator if provided */}
        {teeName && (
          <div className="text-center py-2 bg-emerald-950/30 text-emerald-300/80 text-xs">
            <Flag className="w-3 h-3 inline mr-1" /> {teeName} tees
          </div>
        )}

        <PinDistance key={`${courseId}-${holeNumber}`} courseId={courseId} hole={holeNumber} />

        {/* Swipe hint */}
        <div className="flex items-center justify-center gap-2 text-emerald-400/50 text-xs py-2 bg-emerald-950/40">
          <ChevronLeft className="w-3 h-3" />
          <span>Swipe to dismiss</span>
          <ChevronRight className="w-3 h-3" />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SharedHoleInfoCard;
